import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type OrganizationSettings = Database['public']['Tables']['organization_settings']['Row'];
type OrganizationSettingsInsert = Database['public']['Tables']['organization_settings']['Insert'];
type OrganizationSettingsUpdate = Database['public']['Tables']['organization_settings']['Update'];

export class OrganizationSettingsService {
  private static readonly TABLE = 'organization_settings';
  private static readonly logger = logger.child({ service: 'OrganizationSettingsService' });

  /**
   * Get settings for an organization
   * Returns null if no settings exist yet
   */
  static async get(organizationId: string): Promise<OrganizationSettings | null> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Fetching organization settings', { organizationId });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Organization settings fetched', { 
        organizationId, 
        found: !!data 
      });

      return data;
    }, { operation: 'get', table: this.TABLE });
  }

  /**
   * Create or update organization settings
   */
  static async upsert(
    organizationId: string,
    settings: Partial<OrganizationSettingsUpdate>
  ): Promise<OrganizationSettings> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Upserting organization settings', { organizationId });

      // Check if settings exist
      const existing = await this.get(organizationId);

      if (existing) {
        // Update existing settings
        const { data, error } = await supabase
          .from(this.TABLE)
          .update(settings)
          .eq('organization_id', organizationId)
          .select()
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('No data returned from update');

        this.logger.info('Organization settings updated', { organizationId });
        return data;
      } else {
        // Create new settings
        const { data, error } = await supabase
          .from(this.TABLE)
          .insert({
            organization_id: organizationId,
            ...settings,
          } as OrganizationSettingsInsert)
          .select()
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('No data returned from insert');

        this.logger.info('Organization settings created', { organizationId });
        return data;
      }
    }, { operation: 'upsert', table: this.TABLE });
  }

  /**
   * Update tracking and short link domains
   */
  static async updateDomains(
    organizationId: string,
    domains: { tracking_domain: string; short_link_domain: string }
  ): Promise<OrganizationSettings> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating organization domains', { 
        organizationId, 
        domains 
      });

      // Validate domain formats
      const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
      if (!domainRegex.test(domains.tracking_domain)) {
        throw ServiceError.validation('Invalid tracking domain format', 'tracking_domain');
      }
      if (!domainRegex.test(domains.short_link_domain)) {
        throw ServiceError.validation('Invalid short link domain format', 'short_link_domain');
      }

      return await this.upsert(organizationId, {
        tracking_domain: domains.tracking_domain,
        short_link_domain: domains.short_link_domain,
        custom_domain_enabled: true,
      });
    }, { operation: 'updateDomains', table: this.TABLE });
  }

  /**
   * Toggle a feature for an organization
   */
  static async toggleFeature(
    organizationId: string,
    feature: string,
    enabled: boolean
  ): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Toggling feature', { 
        organizationId, 
        feature, 
        enabled 
      });

      // Get current custom_branding JSON
      const settings = await this.get(organizationId);
      const branding = (settings?.custom_branding as Record<string, unknown>) || {};
      const features = (Array.isArray(branding.enabled_features) ? branding.enabled_features : []) as string[];

      const updatedFeatures = enabled
        ? [...new Set([...features, feature])]
        : features.filter(f => f !== feature);

      await this.upsert(organizationId, {
        custom_branding: {
          ...branding,
          enabled_features: updatedFeatures,
        },
      });

      this.logger.info('Feature toggled', { 
        organizationId, 
        feature, 
        enabled 
      });
    }, { operation: 'toggleFeature', table: this.TABLE });
  }
}
